import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, GitBranch, Save, Send } from 'lucide-react'
import { useEffect, useState } from 'react'
import { apiClient } from '../lib/api'
import { ProposalAssetsTab } from './ProposalAssetsTab'
import { ProposalHistoryDialog } from './ProposalHistoryDialog'
import { Badge } from './ui/Badge'
import { Button } from './ui/Button'
import { useConfirm } from './ui/ConfirmDialog'
import { Drawer } from './ui/Drawer'
import { Input, Label, Select, Textarea } from './ui/Input'

type Proposal = {
  id: string
  code: string
  revision: number
  type: 'PRV' | 'CTR'
  status: string
  title: string
  subtitle: string | null
  totalAmountEur: string | null
  vatRate: string
  validityDays: number
  validUntil: string | null
  content: Record<string, unknown>
}

type Tab =
  | 'meta'
  | 'parti'
  | 'oggetto'
  | 'corrispettivi'
  | 'tempi'
  | 'garanzia'
  | 'articoli'
  | 'firme'
  | 'asset'

const tabs: Array<{ id: Tab; label: string }> = [
  { id: 'meta', label: 'Metadati' },
  { id: 'parti', label: 'Parti' },
  { id: 'oggetto', label: 'Oggetto' },
  { id: 'corrispettivi', label: 'Corrispettivi' },
  { id: 'tempi', label: 'Tempi · Collaudo · Recesso' },
  { id: 'garanzia', label: 'Garanzia · Manutenzione' },
  { id: 'articoli', label: 'Articoli (avanzato)' },
  { id: 'firme', label: 'Firme · Vessatorie' },
  { id: 'asset', label: 'Asset' },
]

// biome-ignore lint/suspicious/noExplicitAny: editing freeform structured content
type ContractContent = any

export function ContractEditorDrawer({
  proposalId,
  onClose,
}: {
  proposalId: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const confirm = useConfirm()
  const [tab, setTab] = useState<Tab>('meta')
  const [content, setContent] = useState<ContractContent | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [meta, setMeta] = useState({
    title: '',
    subtitle: '',
    totalAmountEur: '',
    validityDays: '30',
  })

  const proposalQ = useQuery({
    queryKey: ['proposal', proposalId],
    queryFn: () =>
      apiClient.get<{ found: boolean; proposal: Proposal }>(`/proposals/${proposalId}`),
  })

  useEffect(() => {
    if (proposalQ.data?.found) {
      const p = proposalQ.data.proposal
      setContent(p.content)
      setMeta({
        title: p.title,
        subtitle: p.subtitle ?? '',
        totalAmountEur: p.totalAmountEur ?? '',
        validityDays: String(p.validityDays),
      })
    }
  }, [proposalQ.data])

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiClient.patch(`/proposals/${proposalId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] })
      qc.invalidateQueries({ queryKey: ['proposal', proposalId] })
    },
  })

  const send = useMutation({
    mutationFn: () => apiClient.post(`/proposals/${proposalId}/status`, { status: 'sent' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['proposals'] })
      qc.invalidateQueries({ queryKey: ['proposal', proposalId] })
    },
  })

  function update(path: string[], value: unknown) {
    setContent((prev: ContractContent) => setDeep(prev, path, value))
  }

  function onSave() {
    save.mutate({
      title: meta.title,
      subtitle: meta.subtitle || undefined,
      totalAmountEur: meta.totalAmountEur ? Number(meta.totalAmountEur) : undefined,
      validityDays: Number(meta.validityDays) || 30,
      content,
    })
  }

  if (proposalQ.isLoading || !content) {
    return (
      <Drawer open onClose={onClose} title="Caricamento…" width="lg">
        <div className="p-6 text-zinc-500 text-sm">Caricamento contratto…</div>
      </Drawer>
    )
  }

  if (!proposalQ.data?.found) {
    return (
      <Drawer open onClose={onClose} title="Non trovato" width="md">
        <div className="p-6 text-zinc-500 text-sm">Contratto non trovato.</div>
      </Drawer>
    )
  }

  const p = proposalQ.data.proposal
  const isDraft = p.status === 'draft'

  return (
    <Drawer
      open
      onClose={onClose}
      title={`${p.code} R${p.revision}`}
      subtitle={p.title}
      badges={
        <>
          <Badge variant="brand">CTR</Badge>
          <Badge variant="muted">{p.status}</Badge>
          {!isDraft && <Badge variant="warning">solo lettura</Badge>}
        </>
      }
      width="lg"
    >
      {/* Read-only banner for non-draft contracts */}
      {!isDraft && (
        <div className="mx-4 mt-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
          Questo contratto è in stato <strong>{p.status}</strong> e non può essere modificato. Per
          cambiarne il contenuto crea una nuova revisione (R{p.revision + 1}) dal menu della lista.
          La preview e lo storico restano consultabili.
        </div>
      )}

      {/* Toolbar */}
      <div className="px-4 py-2 border-b border-zinc-800/80 bg-zinc-950/50 flex items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={onSave}
          disabled={save.isPending || !isDraft}
          title={!isDraft ? 'Solo le bozze sono modificabili — crea una nuova revisione' : ''}
        >
          <Save className="size-3.5" />
          {save.isPending ? 'Salvataggio…' : 'Salva'}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => window.open(`/api/proposals/${proposalId}/preview`, '_blank')}
        >
          <Eye className="size-3.5" /> Anteprima
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)}>
          <GitBranch className="size-3.5" /> Storico
        </Button>
        {p.status === 'draft' && (
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              const ok = await confirm({
                title: 'Marcare come inviato?',
                message:
                  'Il contratto risulterà inviato al committente, con la data di oggi. Procedere solo dopo aver effettivamente trasmesso il PDF firmato (PEC, email o consegna).',
                confirmLabel: 'Marca come inviato',
                tone: 'info',
              })
              if (ok) send.mutate()
            }}
            disabled={send.isPending}
          >
            <Send className="size-3.5" /> Marca come inviato
          </Button>
        )}
        {save.isError && <span className="text-xs text-pink-300 ml-auto">Errore salvataggio</span>}
        {save.isSuccess && !save.isPending && (
          <span className="text-xs text-emerald-300 ml-auto">✓ Salvato</span>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-zinc-800/80 flex overflow-x-auto">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap transition border-b-2 -mb-px ${
              tab === t.id
                ? 'text-cyan-300 border-cyan-400'
                : 'text-zinc-500 border-transparent hover:text-zinc-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content — fieldset disabled when status !== draft natively
       * disables every nested form control (see ProposalEditorDrawer for
       * the full rationale). The asset tab also takes readOnly to hide
       * its drag-drop uploader (drops aren't form events). */}
      <fieldset
        disabled={!isDraft}
        className={`contents ${!isDraft ? '[&_input]:cursor-not-allowed [&_select]:cursor-not-allowed [&_textarea]:cursor-not-allowed' : ''}`}
      >
        <div className="p-5 space-y-4">
          {tab === 'meta' && (
            <MetaTab meta={meta} setMeta={setMeta} content={content} update={update} />
          )}
          {tab === 'parti' && <PartiTab content={content} update={update} />}
          {tab === 'oggetto' && <OggettoTab content={content} update={update} />}
          {tab === 'corrispettivi' && <CorrispettiviTab content={content} update={update} />}
          {tab === 'tempi' && <TempiTab content={content} update={update} />}
          {tab === 'garanzia' && <GaranziaTab content={content} update={update} />}
          {tab === 'articoli' && <ArticoliTab content={content} setContent={setContent} />}
          {tab === 'firme' && <FirmeTab content={content} update={update} />}
          {tab === 'asset' && <ProposalAssetsTab proposalId={proposalId} readOnly={!isDraft} />}
        </div>
      </fieldset>

      {historyOpen && (
        <ProposalHistoryDialog
          proposalId={proposalId}
          currentRevision={p.revision}
          onClose={() => setHistoryOpen(false)}
        />
      )}
    </Drawer>
  )
}

// ============================================================
// Tab: Metadata
// ============================================================
function MetaTab({
  meta,
  setMeta,
  content,
  update,
}: {
  meta: { title: string; subtitle: string; totalAmountEur: string; validityDays: string }
  setMeta: (m: typeof meta) => void
  content: ContractContent
  update: (path: string[], v: unknown) => void
}) {
  return (
    <div className="space-y-4">
      <div>
        <Label>Titolo (intestazione)</Label>
        <Input
          value={content.header?.title ?? ''}
          onChange={(e) => update(['header', 'title'], e.target.value)}
        />
      </div>
      <div>
        <Label>Sottotitolo</Label>
        <Input
          value={content.header?.subtitle ?? ''}
          onChange={(e) => update(['header', 'subtitle'], e.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Data emissione</Label>
          <Input
            type="date"
            value={content.header?.issuedOn ?? ''}
            onChange={(e) => update(['header', 'issuedOn'], e.target.value)}
          />
        </div>
        <div>
          <Label>Luogo stipula</Label>
          <Input
            value={content.header?.luogoStipula ?? ''}
            onChange={(e) => update(['header', 'luogoStipula'], e.target.value)}
            placeholder="Formigine (MO)"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Titolo riferimento (DB)</Label>
          <Input value={meta.title} onChange={(e) => setMeta({ ...meta, title: e.target.value })} />
        </div>
        <div>
          <Label>Sottotitolo riferimento (DB)</Label>
          <Input
            value={meta.subtitle}
            onChange={(e) => setMeta({ ...meta, subtitle: e.target.value })}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Importo totale (€, IVA esclusa)</Label>
          <Input
            type="number"
            step="0.01"
            value={meta.totalAmountEur}
            onChange={(e) => setMeta({ ...meta, totalAmountEur: e.target.value })}
          />
        </div>
        <div>
          <Label>Validità (gg)</Label>
          <Input
            type="number"
            value={meta.validityDays}
            onChange={(e) => setMeta({ ...meta, validityDays: e.target.value })}
          />
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Tab: Parti (Fornitore + Committente snapshots)
// ============================================================
function PartiTab({
  content,
  update,
}: {
  content: ContractContent
  update: (path: string[], v: unknown) => void
}) {
  return (
    <div className="space-y-6">
      <PartySnapshotEditor
        label="FORNITORE"
        party={content.parties?.fornitore ?? {}}
        path={['parties', 'fornitore']}
        update={update}
      />
      <PartySnapshotEditor
        label="COMMITTENTE"
        party={content.parties?.committente ?? {}}
        path={['parties', 'committente']}
        update={update}
      />
    </div>
  )
}

function PartySnapshotEditor({
  label,
  party,
  path,
  update,
}: {
  label: string
  // biome-ignore lint/suspicious/noExplicitAny: party shape
  party: any
  path: string[]
  update: (p: string[], v: unknown) => void
}) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 p-4 space-y-3">
      <div className="text-xs uppercase tracking-wider text-cyan-300 font-semibold">{label}</div>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-8">
          <Label>Ragione sociale</Label>
          <Input
            value={party.ragioneSociale ?? ''}
            onChange={(e) => update([...path, 'ragioneSociale'], e.target.value)}
          />
        </div>
        <div className="col-span-4">
          <Label>P.IVA</Label>
          <Input
            value={party.partitaIva ?? ''}
            onChange={(e) => update([...path, 'partitaIva'], e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-6">
          <Label>Indirizzo</Label>
          <Input
            value={party.sedeLegale?.indirizzo ?? ''}
            onChange={(e) => update([...path, 'sedeLegale', 'indirizzo'], e.target.value)}
          />
        </div>
        <div className="col-span-2">
          <Label>CAP</Label>
          <Input
            value={party.sedeLegale?.cap ?? ''}
            onChange={(e) => update([...path, 'sedeLegale', 'cap'], e.target.value)}
          />
        </div>
        <div className="col-span-3">
          <Label>Città</Label>
          <Input
            value={party.sedeLegale?.citta ?? ''}
            onChange={(e) => update([...path, 'sedeLegale', 'citta'], e.target.value)}
          />
        </div>
        <div className="col-span-1">
          <Label>Prov</Label>
          <Input
            value={party.sedeLegale?.provincia ?? ''}
            onChange={(e) => update([...path, 'sedeLegale', 'provincia'], e.target.value)}
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Codice fiscale</Label>
          <Input
            value={party.codiceFiscale ?? ''}
            onChange={(e) => update([...path, 'codiceFiscale'], e.target.value)}
          />
        </div>
        <div>
          <Label>REA</Label>
          <Input
            value={party.rea ?? ''}
            onChange={(e) => update([...path, 'rea'], e.target.value)}
          />
        </div>
        <div>
          <Label>Qualifica</Label>
          <Input
            value={party.qualifica ?? ''}
            onChange={(e) => update([...path, 'qualifica'], e.target.value)}
            placeholder="Titolare / AU"
          />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Legale rappresentante</Label>
          <Input
            value={party.legaleRappresentante ?? ''}
            onChange={(e) => update([...path, 'legaleRappresentante'], e.target.value)}
          />
        </div>
        <div>
          <Label>Email</Label>
          <Input
            value={party.email ?? ''}
            onChange={(e) => update([...path, 'email'], e.target.value)}
          />
        </div>
        <div>
          <Label>PEC</Label>
          <Input
            value={party.pec ?? ''}
            onChange={(e) => update([...path, 'pec'], e.target.value)}
          />
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Tab: Oggetto
// ============================================================
function OggettoTab({
  content,
  update,
}: {
  content: ContractContent
  update: (path: string[], v: unknown) => void
}) {
  const o = content.oggetto ?? { moduliInclusi: [] }
  return (
    <div className="space-y-4">
      <div>
        <Label>Nome software</Label>
        <Input
          value={o.nomeSoftware ?? ''}
          onChange={(e) => update(['oggetto', 'nomeSoftware'], e.target.value)}
        />
      </div>
      <div>
        <Label>Descrizione</Label>
        <Textarea
          rows={3}
          value={o.descrizione ?? ''}
          onChange={(e) => update(['oggetto', 'descrizione'], e.target.value)}
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Allegato A — codice PRV</Label>
          <Input
            value={o.allegatoARef ?? ''}
            onChange={(e) => update(['oggetto', 'allegatoARef'], e.target.value)}
            placeholder="PRV-2026-007 R1"
          />
        </div>
        <div>
          <Label>Allegato A — proposalId (FK)</Label>
          <Input
            value={o.allegatoAProposalId ?? ''}
            onChange={(e) => update(['oggetto', 'allegatoAProposalId'], e.target.value)}
            placeholder="UUID"
          />
        </div>
      </div>
      <ModuleListEditor
        title="Moduli inclusi nel contratto"
        modules={o.moduliInclusi ?? []}
        onChange={(next) => update(['oggetto', 'moduliInclusi'], next)}
      />
      <ModuleListEditor
        title="Moduli esclusi (opzionali, prezzo bloccato 12 mesi)"
        modules={o.moduliEsclusi ?? []}
        onChange={(next) => update(['oggetto', 'moduliEsclusi'], next)}
      />
    </div>
  )
}

type ModuleRow = {
  id: string
  nome: string
  descrizione?: string
  priceEur: number
  vatRate?: number
}

function ModuleListEditor({
  title,
  modules,
  onChange,
}: {
  title: string
  modules: ModuleRow[]
  onChange: (next: ModuleRow[]) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>{title}</Label>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange([...modules, { id: '', nome: '', priceEur: 0 }])}
        >
          + Aggiungi modulo
        </Button>
      </div>
      <div className="space-y-2">
        {modules.map((m, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: editable list
            key={i}
            className="grid grid-cols-12 gap-2"
          >
            <Input
              className="col-span-2"
              value={m.id}
              placeholder="ID"
              onChange={(e) => {
                const next = [...modules]
                const row = next[i] ?? { id: '', nome: '', priceEur: 0 }
                next[i] = { ...row, id: e.target.value }
                onChange(next)
              }}
            />
            <Input
              className="col-span-4"
              value={m.nome}
              placeholder="Nome modulo"
              onChange={(e) => {
                const next = [...modules]
                const row = next[i] ?? { id: '', nome: '', priceEur: 0 }
                next[i] = { ...row, nome: e.target.value }
                onChange(next)
              }}
            />
            <Input
              className="col-span-4"
              value={m.descrizione ?? ''}
              placeholder="Descrizione (opz.)"
              onChange={(e) => {
                const next = [...modules]
                const row = next[i] ?? { id: '', nome: '', priceEur: 0 }
                next[i] = { ...row, descrizione: e.target.value || undefined }
                onChange(next)
              }}
            />
            <Input
              className="col-span-1"
              type="number"
              step="0.01"
              value={m.priceEur ?? 0}
              placeholder="€"
              onChange={(e) => {
                const next = [...modules]
                const row = next[i] ?? { id: '', nome: '', priceEur: 0 }
                next[i] = { ...row, priceEur: Number(e.target.value) || 0 }
                onChange(next)
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(modules.filter((_x: ModuleRow, j: number) => j !== i))}
              className="col-span-1"
            >
              ✕
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================
// Tab: Corrispettivi
// ============================================================
function CorrispettiviTab({
  content,
  update,
}: {
  content: ContractContent
  update: (path: string[], v: unknown) => void
}) {
  const c = content.corrispettivi ?? { tranches: [], coordinate: {} }
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label>Importo in cifre (€)</Label>
          <Input
            type="number"
            step="0.01"
            value={c.importoCifre ?? 0}
            onChange={(e) => update(['corrispettivi', 'importoCifre'], Number(e.target.value) || 0)}
          />
        </div>
        <div>
          <Label>VAT (%)</Label>
          <Input
            type="number"
            value={c.vatRate ?? 22}
            onChange={(e) => update(['corrispettivi', 'vatRate'], Number(e.target.value) || 22)}
          />
        </div>
        <div>
          <Label>Mora annua (%)</Label>
          <Input
            type="number"
            value={c.interesseMoraTassoAnnuo ?? 8}
            onChange={(e) =>
              update(['corrispettivi', 'interesseMoraTassoAnnuo'], Number(e.target.value) || 8)
            }
          />
        </div>
      </div>
      <div>
        <Label>Importo in lettere</Label>
        <Input
          value={c.importoLettere ?? ''}
          onChange={(e) => update(['corrispettivi', 'importoLettere'], e.target.value)}
          placeholder="seimiladuecento"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <Label>Tranche di pagamento</Label>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              update(
                ['corrispettivi', 'tranches'],
                [...(c.tranches ?? []), { label: '', percentuale: 0 }],
              )
            }
          >
            + Aggiungi tranche
          </Button>
        </div>
        <div className="space-y-2">
          {(c.tranches ?? []).map(
            (
              t: {
                label: string
                percentuale?: number
                importoEur?: number
                importoLettere?: string
                condizione?: string
              },
              i: number,
            ) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: editable list
                key={i}
                className="grid grid-cols-12 gap-2"
              >
                <Input
                  className="col-span-4"
                  value={t.label}
                  placeholder="Es. Acconto alla firma"
                  onChange={(e) => {
                    const next = [...(c.tranches ?? [])]
                    next[i] = { ...next[i], label: e.target.value }
                    update(['corrispettivi', 'tranches'], next)
                  }}
                />
                <Input
                  className="col-span-1"
                  type="number"
                  value={t.percentuale ?? ''}
                  placeholder="%"
                  onChange={(e) => {
                    const next = [...(c.tranches ?? [])]
                    next[i] = {
                      ...next[i],
                      percentuale: e.target.value ? Number(e.target.value) : undefined,
                    }
                    update(['corrispettivi', 'tranches'], next)
                  }}
                />
                <Input
                  className="col-span-2"
                  type="number"
                  step="0.01"
                  value={t.importoEur ?? ''}
                  placeholder="€"
                  onChange={(e) => {
                    const next = [...(c.tranches ?? [])]
                    next[i] = {
                      ...next[i],
                      importoEur: e.target.value ? Number(e.target.value) : undefined,
                    }
                    update(['corrispettivi', 'tranches'], next)
                  }}
                />
                <Input
                  className="col-span-4"
                  value={t.condizione ?? ''}
                  placeholder="Condizione (opz.)"
                  onChange={(e) => {
                    const next = [...(c.tranches ?? [])]
                    next[i] = { ...next[i], condizione: e.target.value || undefined }
                    update(['corrispettivi', 'tranches'], next)
                  }}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    update(
                      ['corrispettivi', 'tranches'],
                      (c.tranches ?? []).filter((_x: unknown, j: number) => j !== i),
                    )
                  }}
                  className="col-span-1"
                >
                  ✕
                </Button>
              </div>
            ),
          )}
        </div>
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <Label>Coordinate bancarie</Label>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Input
            placeholder="Intestatario"
            value={c.coordinate?.intestatario ?? ''}
            onChange={(e) =>
              update(['corrispettivi', 'coordinate', 'intestatario'], e.target.value)
            }
          />
          <Input
            placeholder="IBAN"
            value={c.coordinate?.iban ?? ''}
            onChange={(e) => update(['corrispettivi', 'coordinate', 'iban'], e.target.value)}
          />
          <Input
            placeholder="Istituto"
            value={c.coordinate?.istituto ?? ''}
            onChange={(e) => update(['corrispettivi', 'coordinate', 'istituto'], e.target.value)}
          />
          <Input
            placeholder="Indirizzo banca"
            value={c.coordinate?.indirizzoBanca ?? ''}
            onChange={(e) =>
              update(['corrispettivi', 'coordinate', 'indirizzoBanca'], e.target.value)
            }
          />
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Tab: Tempi + Collaudo + Durata
// ============================================================
function TempiTab({
  content,
  update,
}: {
  content: ContractContent
  update: (path: string[], v: unknown) => void
}) {
  const t = content.tempistiche ?? {}
  const cl = content.collaudo ?? {}
  const d = content.durataRecesso ?? {}
  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-sm font-semibold text-zinc-100 mb-2">Tempistiche (Art. 4)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Durata indicativa (settimane)</Label>
            <Input
              type="number"
              value={t.durataIndicativaSettimane ?? 8}
              onChange={(e) =>
                update(['tempistiche', 'durataIndicativaSettimane'], Number(e.target.value) || 8)
              }
            />
          </div>
          <div>
            <Label>Decorrenza da</Label>
            <Select
              value={t.decorrenzaDa ?? 'anticipo'}
              onChange={(e) => update(['tempistiche', 'decorrenzaDa'], e.currentTarget.value)}
            >
              <option value="firma">Firma del contratto</option>
              <option value="anticipo">Incasso acconto</option>
              <option value="datiCliente">Ricezione dati cliente</option>
            </Select>
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <h3 className="text-sm font-semibold text-zinc-100 mb-2">Collaudo (Art. 5)</h3>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Giorni collaudo</Label>
            <Input
              type="number"
              value={cl.giorniCollaudo ?? 15}
              onChange={(e) => update(['collaudo', 'giorniCollaudo'], Number(e.target.value) || 15)}
            />
          </div>
          <div>
            <Label>Max cicli revisione</Label>
            <Input
              type="number"
              value={cl.maxCicliRevisione ?? 3}
              onChange={(e) =>
                update(['collaudo', 'maxCicliRevisione'], Number(e.target.value) || 3)
              }
            />
          </div>
          <div>
            <Label>Giorni accettazione tacita</Label>
            <Input
              type="number"
              value={cl.accettazioneTacitaGiorni ?? 15}
              onChange={(e) =>
                update(['collaudo', 'accettazioneTacitaGiorni'], Number(e.target.value) || 15)
              }
            />
          </div>
        </div>
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <h3 className="text-sm font-semibold text-zinc-100 mb-2">Durata e recesso (Art. 6)</h3>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Efficacia da</Label>
            <Select
              value={d.efficaciaDa ?? 'sottoscrizione'}
              onChange={(e) => update(['durataRecesso', 'efficaciaDa'], e.currentTarget.value)}
            >
              <option value="sottoscrizione">Sottoscrizione</option>
              <option value="anticipo">Incasso acconto</option>
              <option value="completamentoFaseAvvio">Completamento fase avvio</option>
            </Select>
          </div>
          <div>
            <Label>Penale recesso committente (% sul residuo)</Label>
            <Input
              type="number"
              value={d.recessoCommittentePenalePercResiduo ?? 20}
              onChange={(e) =>
                update(
                  ['durataRecesso', 'recessoCommittentePenalePercResiduo'],
                  Number(e.target.value) || 20,
                )
              }
            />
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================
// Tab: Garanzia + Manutenzione
// ============================================================
function GaranziaTab({
  content,
  update,
}: {
  content: ContractContent
  update: (path: string[], v: unknown) => void
}) {
  const g = content.garanziaManutenzione ?? { garanziaInclude: [], garanziaEsclude: [] }
  return (
    <div className="space-y-4">
      <div>
        <Label>Garanzia (mesi)</Label>
        <Input
          type="number"
          value={g.garanziaMesi ?? 12}
          onChange={(e) =>
            update(['garanziaManutenzione', 'garanziaMesi'], Number(e.target.value) || 12)
          }
        />
      </div>
      <StringListEditor
        label="La garanzia include"
        items={g.garanziaInclude ?? []}
        onChange={(next) => update(['garanziaManutenzione', 'garanziaInclude'], next)}
      />
      <StringListEditor
        label="La garanzia esclude"
        items={g.garanziaEsclude ?? []}
        onChange={(next) => update(['garanziaManutenzione', 'garanziaEsclude'], next)}
      />

      <div className="border-t border-zinc-800 pt-4">
        <Label>Infrastruttura (opzionale)</Label>
        <div className="grid grid-cols-3 gap-3 mt-2">
          <Select
            value={g.infrastruttura?.tipo ?? 'nessuna'}
            onChange={(e) =>
              update(['garanziaManutenzione', 'infrastruttura', 'tipo'], e.currentTarget.value)
            }
          >
            <option value="nessuna">Nessuna</option>
            <option value="vps">VPS dedicata</option>
            <option value="shared">Shared hosting</option>
            <option value="cliente">A carico cliente</option>
          </Select>
          <Input
            type="number"
            step="0.01"
            placeholder="Canone annuo €"
            value={g.infrastruttura?.canoneAnnuoEur ?? ''}
            onChange={(e) =>
              update(
                ['garanziaManutenzione', 'infrastruttura', 'canoneAnnuoEur'],
                e.target.value ? Number(e.target.value) : undefined,
              )
            }
          />
          <Input
            type="number"
            placeholder="Mesi gratis iniziali"
            value={g.infrastruttura?.primiMesiGratis ?? ''}
            onChange={(e) =>
              update(
                ['garanziaManutenzione', 'infrastruttura', 'primiMesiGratis'],
                e.target.value ? Number(e.target.value) : undefined,
              )
            }
          />
        </div>
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <Label>Manutenzione evolutiva — tariffa oraria (€)</Label>
        <Input
          type="number"
          step="0.01"
          value={g.manutenzioneEvolutiva?.tariffaOraria ?? ''}
          onChange={(e) =>
            update(
              ['garanziaManutenzione', 'manutenzioneEvolutiva', 'tariffaOraria'],
              e.target.value ? Number(e.target.value) : undefined,
            )
          }
        />
      </div>
    </div>
  )
}

// ============================================================
// Tab: Articoli — JSON editor avanzato per articoliExtra
// ============================================================
function ArticoliTab({
  content,
  setContent,
}: {
  content: ContractContent
  setContent: (next: ContractContent) => void
}) {
  const [jsonText, setJsonText] = useState(() =>
    JSON.stringify(content.articoliExtra ?? [], null, 2),
  )
  const [err, setErr] = useState<string | null>(null)

  function apply() {
    try {
      const parsed = JSON.parse(jsonText)
      if (!Array.isArray(parsed)) {
        setErr('Deve essere un array di articoli extra')
        return
      }
      setContent({ ...content, articoliExtra: parsed })
      setErr(null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'JSON non valido')
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-zinc-500 leading-relaxed">
        Articoli base (1-18) sono generati automaticamente dal renderer usando i valori delle altre
        tab. Qui puoi aggiungere <strong>articoli extra</strong> caso-specifici (SLA, NDA,
        condizioni speciali) come array JSON. Se <code>isVessatoria=true</code>, l&apos;articolo
        viene automaticamente elencato in Art. 18 (clausole vessatorie 1341/1342 c.c.).
      </p>
      <Textarea
        value={jsonText}
        onChange={(e) => setJsonText(e.target.value)}
        rows={20}
        className="font-mono text-xs"
        spellCheck={false}
      />
      {err && (
        <div className="text-sm text-pink-300 rounded-md bg-pink-500/10 border border-pink-500/30 px-3 py-2">
          {err}
        </div>
      )}
      <div className="flex gap-2">
        <Button variant="secondary" size="sm" onClick={apply}>
          Applica al content
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setJsonText(JSON.stringify(content.articoliExtra ?? [], null, 2))}
        >
          Ripristina
        </Button>
      </div>
    </div>
  )
}

// ============================================================
// Tab: Firme + vessatorie
// ============================================================
function FirmeTab({
  content,
  update,
}: {
  content: ContractContent
  update: (path: string[], v: unknown) => void
}) {
  const f = content.firme ?? { committente: {}, fornitore: {} }
  const cv = content.clausoleVessatorie ?? { articoliApprovatiNumeri: [] }
  return (
    <div className="space-y-5">
      <div>
        <Label>Luogo e data</Label>
        <Input
          value={f.luogoData ?? ''}
          onChange={(e) => update(['firme', 'luogoData'], e.target.value)}
          placeholder="Formigine, 12/05/2026"
        />
      </div>

      <div className="rounded-lg border border-zinc-800 p-4 space-y-2">
        <h3 className="text-sm font-semibold text-zinc-100">Firma committente</h3>
        <Input
          placeholder="Ragione sociale"
          value={f.committente?.ragioneSociale ?? ''}
          onChange={(e) => update(['firme', 'committente', 'ragioneSociale'], e.target.value)}
        />
        <Input
          placeholder="Legale rappresentante"
          value={f.committente?.legaleRappresentante ?? ''}
          onChange={(e) => update(['firme', 'committente', 'legaleRappresentante'], e.target.value)}
        />
        <Input
          placeholder="Qualifica (Titolare / AU)"
          value={f.committente?.qualifica ?? ''}
          onChange={(e) => update(['firme', 'committente', 'qualifica'], e.target.value)}
        />
      </div>

      <div className="rounded-lg border border-zinc-800 p-4 space-y-2">
        <h3 className="text-sm font-semibold text-zinc-100">Firma fornitore (G soLutions)</h3>
        <Input
          placeholder="Ragione sociale"
          value={f.fornitore?.ragioneSociale ?? ''}
          onChange={(e) => update(['firme', 'fornitore', 'ragioneSociale'], e.target.value)}
        />
        <Input
          placeholder="Legale rappresentante"
          value={f.fornitore?.legaleRappresentante ?? ''}
          onChange={(e) => update(['firme', 'fornitore', 'legaleRappresentante'], e.target.value)}
        />
        <Input
          placeholder="Qualifica"
          value={f.fornitore?.qualifica ?? ''}
          onChange={(e) => update(['firme', 'fornitore', 'qualifica'], e.target.value)}
        />
        <p className="text-[11px] text-zinc-500 italic">
          Firma e timbro vengono caricati dal renderer dall'asset MinIO indicato in{' '}
          <code>tenant_config.branding.firmaAssetKey</code> e <code>timbroAssetKey</code> (file
          caricati via <code>POST /api/storage/branding/:filename</code>).
        </p>
      </div>

      <div className="border-t border-zinc-800 pt-4">
        <Label>Clausole vessatorie (Art. 18) — numeri articoli</Label>
        <p className="text-xs text-zinc-500 mb-2">
          Lista comma-separated dei numeri articoli approvati specificamente ai sensi degli artt.
          1341 e 1342 c.c. (es. <code>3, 6, 11, 13, 14</code>).
        </p>
        <Input
          value={(cv.articoliApprovatiNumeri ?? []).join(', ')}
          onChange={(e) => {
            const nums = e.target.value
              .split(',')
              .map((s) => Number(s.trim()))
              .filter((n) => Number.isFinite(n) && n > 0)
            update(['clausoleVessatorie', 'articoliApprovatiNumeri'], nums)
          }}
          placeholder="3, 6, 11, 13, 14"
        />
      </div>
    </div>
  )
}

// ============================================================
// Reusable string list editor
// ============================================================
function StringListEditor({
  label,
  items,
  onChange,
}: {
  label: string
  items: string[]
  onChange: (next: string[]) => void
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <Label>{label}</Label>
        <Button variant="ghost" size="sm" onClick={() => onChange([...items, ''])}>
          + Aggiungi
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((s, i) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: editable string list
            key={i}
            className="flex gap-2"
          >
            <Input
              className="flex-1"
              value={s}
              onChange={(e) => {
                const next = [...items]
                next[i] = e.target.value
                onChange(next)
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onChange(items.filter((_x: string, j: number) => j !== i))}
            >
              ✕
            </Button>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================
// helpers
// ============================================================
function setDeep(obj: ContractContent, path: string[], value: unknown): ContractContent {
  if (path.length === 0) return value as ContractContent
  const head = path[0]
  const rest = path.slice(1)
  if (head === undefined) return value as ContractContent
  const current = (obj?.[head] as ContractContent) ?? {}
  return {
    ...(obj ?? {}),
    [head]: rest.length === 0 ? value : setDeep(current, rest, value),
  }
}
